import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type SupportThread, type SupportMessage } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column, type BadgeTone } from '@/components/ui';
import { IconChat } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';
import { useAdminPermission } from '@/lib/useAdminPermission';
import { getAdminProfile } from '@/lib/adminAuth';

const PAGE = 25;

function threadStatusBadge(status: string) {
  const tone: BadgeTone = status === 'open' ? 'warn' : status === 'closed' ? 'neutral' : 'info';
  return <Badge tone={tone} dot>{status || 'unknown'}</Badge>;
}

/** Support — the agent inbox: list/filter support_threads (one ongoing thread
 *  per customer). A "View" opens the transcript drawer, where a staffer with
 *  support.manage can reply, claim the thread, or close/reopen it (those writes
 *  proxy support-svc, which owns the threads/messages tables). DEFENSIVE: shows
 *  a clean "not yet available" state when support_threads isn't in this Postgres.
 *  Covers the Filament support-chat queue list/view + agent reply/claim/close. */
export function Support() {
  const [status, setStatus] = useState<string>(''); // '' | open | closed
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<SupportThread | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'support', status, offset],
    queryFn: ({ signal }) => adminApi.supportThreads({ status, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const columns: Array<Column<SupportThread>> = [
    { key: 'cust', header: 'Customer', cell: (r) => <span className="metric text-sm font-medium text-slate-100">#{r.customer_id}</span> },
    { key: 'status', header: 'Status', cell: (r) => threadStatusBadge(r.status) },
    { key: 'preview', header: 'Last message', cell: (r) => <span className="truncate text-xs text-slate-300" title={r.last_preview}>{r.last_preview || '—'}</span> },
    {
      key: 'unread',
      header: 'Unread',
      align: 'right',
      cell: (r) => (r.unread_agent > 0 ? <Badge tone="info">{r.unread_agent}</Badge> : <span className="text-xs text-muted">0</span>),
    },
    { key: 'agent', header: 'Agent', cell: (r) => <span className="metric text-xs text-slate-300">{r.assigned_agent_id ? `#${r.assigned_agent_id}` : 'unclaimed'}</span> },
    { key: 'when', header: 'Last active', cell: (r) => <span className="text-xs text-muted">{dateTime(r.last_message_at)}</span> },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          View
        </button>
      ),
    },
  ];

  const unavailable = data && data.available === false;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Support tickets"
        subtitle="Customer ↔ support threads — the agent inbox. Open one to reply, claim, or close it."
        right={
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setOffset(0);
            }}
            className="rounded-lg border border-ink-700 bg-ink-850 px-3 py-1.5 text-sm text-slate-200 focus:border-brand-500 focus:outline-none"
          >
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Support threads not yet in Postgres"
            description={data?.note ?? 'The support_threads table is owned by support-svc and is not in this Postgres yet. This page lights up automatically once it lands.'}
            icon={<IconChat className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <Card flush>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner label="Loading support threads…" />
            </div>
          ) : isError ? (
            <EmptyState title="Couldn't load support threads" description={(error as Error)?.message} />
          ) : (
            <>
              <DataTable<SupportThread>
                columns={columns}
                rows={data?.items ?? []}
                rowKey={(r) => String(r.customer_id)}
                stickyHeader
                empty={<EmptyState title="No support threads" description="No threads match this filter." icon={<IconChat className="h-6 w-6" />} />}
              />
              <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
            </>
          )}
        </Card>
      )}

      {selected !== null ? <SupportThreadDrawer thread={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

/** SupportThreadDrawer — the message transcript for one thread plus, for a
 *  staffer with support.manage, the agent action panel (claim / close / reply).
 *  The thread row passed in carries status + assigned_agent_id, so the header
 *  and claim/close controls render without a second fetch; the transcript is
 *  fetched here and refetched after a reply so the new message appears. */
function SupportThreadDrawer({ thread, onClose }: { thread: SupportThread; onClose: () => void }) {
  const customerId = thread.customer_id;

  // Track status + assignee locally so the header + controls reflect a claim /
  // close immediately, before the background list refetch round-trips.
  const [status, setStatus] = useState<string>(thread.status);
  const [assignedAgentId, setAssignedAgentId] = useState<number | null>(thread.assigned_agent_id);

  const { data, isLoading, isError, error, refetch, isRefetching } = useQuery({
    queryKey: ['admin', 'support', 'thread', customerId],
    queryFn: ({ signal }) => adminApi.supportThread(customerId, signal),
  });

  const messages = data?.messages ?? [];
  const threadUnavailable = data && data.available === false;

  return (
    <Drawer
      title={`Thread · customer #${customerId}`}
      subtitle={
        <span className="inline-flex items-center gap-2">
          {threadStatusBadge(status)}
          <span>·</span>
          <span>{assignedAgentId ? `agent #${assignedAgentId}` : 'unclaimed'}</span>
        </span>
      }
      onClose={onClose}
    >
      <div className="space-y-4">
        <div>
          <Field label="Customer" value={`#${customerId}`} />
          <Field label="Status" value={threadStatusBadge(status)} />
          <Field label="Assigned agent" value={assignedAgentId ? `#${assignedAgentId}` : 'unclaimed'} />
          <Field label="Last active" value={dateTime(thread.last_message_at)} />
        </div>

        <SupportAgentActions
          customerId={customerId}
          status={status}
          assignedAgentId={assignedAgentId}
          onStatusChange={setStatus}
          onAssignedChange={setAssignedAgentId}
          onReplied={() => refetch()}
        />

        <Card flush>
          <div className="border-b border-ink-800 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted">
            Transcript {isRefetching ? <span className="ml-2 normal-case text-muted">refreshing…</span> : null}
          </div>
          <div className="p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-10">
                <Spinner label="Loading transcript…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load transcript" description={(error as Error)?.message} />
            ) : threadUnavailable ? (
              <EmptyState
                title="Support threads not yet in Postgres"
                description={data?.note ?? 'The support_threads table is owned by support-svc and is not in this Postgres yet.'}
              />
            ) : messages.length === 0 ? (
              <EmptyState title="No messages" description="This thread has no messages yet." />
            ) : (
              <div className="space-y-2">
                {messages.map((m) => (
                  <MessageBubble key={m.id} message={m} />
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>
    </Drawer>
  );
}

/** SupportAgentActions — the gated agent write surface for one thread: claim it,
 *  close/reopen it, and reply. All three proxy support-svc through admin-svc
 *  (support-svc owns the threads/messages tables) carrying the acting staffer as
 *  the agent identity, so the customer sees the reply in real time. On success
 *  each lifts the new state to the drawer and invalidates the threads list so the
 *  inbox row (status / preview / unread / agent) stays in sync.
 *
 *  RBAC: only ops + superadmin hold support.manage, so other roles see a
 *  read-only note instead of the controls. (admin-svc enforces the same matrix
 *  and 403s regardless — this is a UX affordance, not the security boundary.) */
function SupportAgentActions({
  customerId,
  status,
  assignedAgentId,
  onStatusChange,
  onAssignedChange,
  onReplied,
}: {
  customerId: number;
  status: string;
  assignedAgentId: number | null;
  onStatusChange: (status: string) => void;
  onAssignedChange: (id: number | null) => void;
  onReplied: () => void;
}) {
  const canManage = useAdminPermission('support.manage');
  const qc = useQueryClient();
  const me = getAdminProfile();
  const claimedByMe = assignedAgentId != null && me != null && assignedAgentId === me.id;

  const [body, setBody] = useState('');

  // Refresh the inbox list so the row's status / preview / unread / agent reflect
  // any write. Cheap — the list query refetches in the background.
  const refreshList = () => qc.invalidateQueries({ queryKey: ['admin', 'support'] });

  const reply = useMutation({
    mutationFn: () => adminApi.supportReply(customerId, body.trim()),
    onSuccess: () => {
      setBody('');
      onReplied();
      refreshList();
    },
  });

  const claim = useMutation({
    mutationFn: () => adminApi.supportClaim(customerId),
    onSuccess: (res) => {
      onAssignedChange(res.assigned_agent_id);
      refreshList();
    },
  });

  const close = useMutation({
    mutationFn: (next: 'open' | 'closed') => adminApi.supportClose(customerId, next),
    onSuccess: (res) => {
      onStatusChange(res.new_status);
      refreshList();
    },
  });

  if (!canManage) {
    return (
      <Card>
        <p className="text-xs text-muted">
          Read-only — your role can review support threads but cannot reply, claim, or close them.
          (Those actions require the <code className="text-slate-400">ops</code> role.)
        </p>
      </Card>
    );
  }

  const busy = reply.isPending || claim.isPending || close.isPending;
  const trimmed = body.trim();
  const tooLong = trimmed.length > 1000;
  const reopen = status === 'closed';

  // The single shared error line (whichever write last failed). Reply errors
  // render under the composer; claim/close errors render next to their buttons.
  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-medium text-slate-100">Agent actions</div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            disabled={busy || claimedByMe}
            onClick={() => claim.mutate()}
            className={
              claimedByMe
                ? 'rounded-md bg-ink-850 px-2.5 py-1 text-xs font-medium text-muted ring-1 ring-inset ring-ink-700'
                : 'rounded-md bg-ink-850 px-2.5 py-1 text-xs font-medium text-slate-200 ring-1 ring-inset ring-ink-700 hover:bg-ink-800 disabled:opacity-50'
            }
            title={claimedByMe ? 'You are assigned to this thread' : 'Assign this thread to yourself'}
          >
            {claim.isPending ? 'Claiming…' : claimedByMe ? 'Claimed by you' : assignedAgentId ? 'Reassign to me' : 'Claim'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => close.mutate(reopen ? 'open' : 'closed')}
            className="rounded-md bg-ink-850 px-2.5 py-1 text-xs font-medium text-slate-200 ring-1 ring-inset ring-ink-700 hover:bg-ink-800 disabled:opacity-50"
            title={reopen ? 'Reopen this thread' : 'Mark this thread closed'}
          >
            {close.isPending ? 'Saving…' : reopen ? 'Reopen' : 'Close thread'}
          </button>
        </div>
      </div>

      {claim.isError ? (
        <p className="mb-2 text-xs text-danger">{(claim.error as Error)?.message}</p>
      ) : null}
      {close.isError ? (
        <p className="mb-2 text-xs text-danger">{(close.error as Error)?.message}</p>
      ) : null}

      <label className="block text-[11px] text-muted">Reply to the customer</label>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        placeholder="Type a reply… the customer sees it in real time."
        className="mt-1 resize-y input"
      />
      <div className="mt-1 flex items-center justify-between text-[11px]">
        <span className={tooLong ? 'text-danger' : 'text-muted'}>{trimmed.length}/1000</span>
        {!assignedAgentId ? (
          <span className="text-muted">Tip: claim the thread so the customer sees who's helping.</span>
        ) : null}
      </div>

      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          disabled={busy || trimmed === '' || tooLong}
          onClick={() => reply.mutate()}
          className="btn-primary"
        >
          {reply.isPending ? 'Sending…' : 'Send reply'}
        </button>
        {reply.isSuccess ? <span className="text-xs text-ok">Reply sent.</span> : null}
        {reply.isError ? <span className="text-xs text-danger">{(reply.error as Error)?.message}</span> : null}
      </div>
    </Card>
  );
}

function MessageBubble({ message: m }: { message: SupportMessage }) {
  const isAgent = m.sender_role === 'agent';
  return (
    <div className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-lg px-3 py-2 ${
          isAgent ? 'bg-brand-600/15 ring-1 ring-inset ring-brand-600/30' : 'bg-ink-850 ring-1 ring-inset ring-ink-800'
        }`}
      >
        <div className="mb-0.5 flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted">
          <span>{isAgent ? m.agent_name || `agent #${m.sender_id}` : `customer #${m.sender_id}`}</span>
          <span>·</span>
          <span>{dateTime(m.created_at)}</span>
        </div>
        {m.body ? <p className="whitespace-pre-wrap text-sm text-slate-200">{m.body}</p> : null}
        {m.attachment_url ? (
          <a href={m.attachment_url} target="_blank" rel="noreferrer" className="text-xs text-brand-400 hover:underline">
            view attachment
          </a>
        ) : null}
      </div>
    </div>
  );
}
